import { defineStore } from 'pinia'
import { AddSubscriptionResponse, ProductPrice, Subscription, SubscriptionItem } from '@webapp/gp/GroupAdminModel'
import { useGroupAdminApi } from './GroupAdminApi'
import { usePaymentMethodStore } from './PaymentMethodStore'
import { useUserStore } from './UserStore'
import { AsyncData, LoadMode } from '@webapp/util/AsyncData'
import { AsyncDataStore } from '@webapp/util/AsyncDataStore'
import { useTokenManager } from '@webapp/auth/TokenManager'
import { assignExisting, isDefined } from '@webapp/util/TypeScriptUtil'
import { DateTimeUtil } from '@webapp/util/LuxonUtil'
import { DateTime } from 'luxon'
import { uniqBy } from 'lodash'

export const useSubscriptionStore = defineStore('subscriptions', () => {
    const asyncSubscriptions = new Map<string, AsyncData<Subscription>>()

    const gp = useGroupAdminApi()
    const userStore = useUserStore()
    const paymentMethodStore = usePaymentMethodStore()
    const tokenManager = useTokenManager()

    function getLoadedSubscription(id: string | undefined) {
        const store = useSubscriptionStore() // use to get/return reactive store objects
        return store.asyncSubscriptions.get(id ?? '')?.data
    }

    function getLoadedSubscriptionList(ids: (string | undefined)[]) {
        const store = useSubscriptionStore() // use to get/return reactive store objects
        return ids.map(id => store.asyncSubscriptions.get(id ?? '')).map(a => a?.data).filter(isDefined)
    }

    function getAsyncKeyListForUser(userId: string | undefined, loadMode = LoadMode.TrackOnly) {
        userId ??= tokenManager.current.userId
        const store = useSubscriptionStore() // use to get/return reactive store objects
        const loadAsync = async (ids: string[], requestTime: DateTime) => {
            const path = `users/${userId}/subscriptions`
            const plainSubs = await gp.getPlainCollectionAsync(path, "paymentMethod")
            console.log(`Loaded ${plainSubs.length} payment methods at ${path}`)
            return processExpandedSubscriptions(plainSubs, requestTime)
        }
        return AsyncDataStore.getAsyncKeyListForRelatedItems(userId, "User",
            userStore.asyncSubscriptionKeys, store.asyncSubscriptions, "Subscription", loadMode, loadAsync)
    }

    function getAsyncKeyListForSelf(loadMode = LoadMode.TrackOnly) {
        // NOTE: use the actual user id, because we don't want $me as a cache key
        return getAsyncKeyListForUser(tokenManager.current.userId, loadMode)
    }

    function getSubscriptionListForSelf(loadMode = LoadMode.TrackOnly) {
        return getSubscriptionListForUser(undefined, loadMode)
    }

    function getSubscriptionListForUser(userId: string | undefined, loadMode = LoadMode.TrackOnly) {
        const keys = getAsyncKeyListForUser(userId, loadMode)?.data?.keys ?? []
        return getLoadedSubscriptionList(keys)
    }

    async function getSubscriptionListForUserAsync(userId: string | undefined) {
        const keyList = await getAsyncKeyListForUser(userId, LoadMode.EnsureLoaded)?.whenLoadCompleted
        return getLoadedSubscriptionList(keyList?.keys ?? [])
    }

    async function addAsync(planId: string) {
        const plainResponse = await gp.postPlainAsync('subscriptions', undefined, { planId })
        return plainResponse as AddSubscriptionResponse
    }

    async function changePlanAsync(subscriptionId: string, planId: string) {
         await gp.postPlainAsync(`subscriptions/${subscriptionId}/changeplan`, undefined, { planId })
    }
    
    async function cancelAsync(subscriptionId: string) {
        await gp.postPlainAsync(`subscriptions/${subscriptionId}/cancel`)
    }

    function plainToSubscription(p: any) {
        const sub = assignExisting(new Subscription(), p)
        sub.startDate = DateTimeUtil.fromAPI(p.startDate)
        sub.currentPeriodStart = DateTimeUtil.fromAPI(p.currentPeriodStart)
        sub.currentPeriodEnd = DateTimeUtil.fromAPI(p.currentPeriodEnd)
        sub.createdDate = DateTimeUtil.fromAPI(p.createdDate)
        sub.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate)
        sub.items = p.items?.map((pi: any) => plainToSubscriptionItem(pi)) ?? []
        return sub
    }

    function plainToSubscriptionItem(p: any) {
        const item = assignExisting(new SubscriptionItem(), p)
        item.price = plainToProductPrice(p.price)
        return item
    }

    function plainToProductPrice(p: any) {
        const price = assignExisting(new ProductPrice(), p)
        return price
    }

    function addSubscriptionsToStore(plainSubs: any[], requestTime: DateTime) {
        const store = useSubscriptionStore() // use to get/return reactive store objects
        const subscriptionMap = processExpandedSubscriptions(uniqBy(plainSubs, 'id'), requestTime)
        AsyncDataStore.addItemMapToStore(store.asyncSubscriptions, "Subscription", subscriptionMap, requestTime)
        return subscriptionMap
    }

    function processExpandedSubscriptions(plainSubs: any[], requestTime: DateTime) {
        paymentMethodStore.addPaymentMethodsToStore(plainSubs.map(p => p.paymentMethod).filter(isDefined), requestTime)
        return AsyncDataStore.createItemMap(plainSubs, "Subscription", plainToSubscription, s => s.id!)
    }

    return {
        asyncSubscriptions,

        getLoadedSubscription,
        getLoadedSubscriptionList,
        getAsyncKeyListForUser,
        getAsyncKeyListForSelf,
        getSubscriptionListForSelf,
        getSubscriptionListForUser,
        getSubscriptionListForUserAsync,
        addAsync,
        changePlanAsync,
        cancelAsync,
        addSubscriptionsToStore,
    }
})
